import { isEmptyDisplayValue } from '../FieldWrapper/isEmptyDisplayValue';
import FormFieldView from '../FormFieldView/FormFieldView';
import { getDocumentRequiredMessage } from '@utils/validation/document/requiredMessage';
import { UseFormReturn } from 'react-hook-form';
import clsx from 'clsx';
import { formulaTraceAttrs } from '@/context/DocumentFormulaTraceContext';
import { IDocumentAutoComplete } from '@models/Forms/IForms';
import FieldWrapper from '../FieldWrapper/FieldWrapper';
import './AutoComplete.scss';
import { IFieldElem } from '@models/IFormData';
import { useContext, useEffect, useMemo } from 'react';
import { FormMessageReserveContext } from '../../FormMessageReserveContext';
import { useRegisterFieldChanges, useRunFieldChanges } from '@controls/ChangeHandlers/useChangeHandlers';
import DisplayField from '@molecules/DisplayField/DisplayField';
import { ValueType } from '@srm/ui/types/ValueType';
import AutoCompleteControl from '@srm/ui/controls/AutoCompleteControl';
import ToolTipTitle from '@atoms/ToolTipTitle/ToolTipTitle';
import { getOptions } from './AutoCompleteHelper';
import { IPartialFormGroup } from '@srm/ui/controls/types';
import {
    useFormulaState,
    useFormulaStateWithStatus,
    useFieldSaver,
    useClearErrorOnOptional,
    useActivated,
} from '@molecules/formbuilder/hooks';
import { useDebugPanel } from '@/context/DebugPanelContext';
import FieldFormulaTooltip from '@molecules/DebugPanel/FieldFormulaTooltip/FieldFormulaTooltip';
import { EnsureExternalDataSourceLoaded } from '@/utils/externalDataSourceUtils';

export interface IAutoCompleteProps<TFieldValues extends object = object> {
    autoComplete?: IDocumentAutoComplete;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
}

const AutoComplete = <TFieldValues extends object = object>({
    autoComplete,
    formMethods,
    setError,
    isEdit,
    isNew,
    fields,
    includedFields,
    ensureExternalDataSourceLoaded,
}: IAutoCompleteProps<TFieldValues>) => {
    const { activated } = useActivated();
    const { settings: debugSettings, fieldOverrides } = useDebugPanel();
    const reserveMessage = useContext(FormMessageReserveContext);
    const fieldOverride = autoComplete?.key ? fieldOverrides.get(autoComplete.key) : undefined;
    const visibilityFromFormula = useFormulaState(
        autoComplete?.visibilityRules,
        fields,
        formMethods,
        isEdit,
        isNew,
        activated,
        {
            fallback: true,
            ensureExternalDataSourceLoaded,
            trace: autoComplete
                ? {
                      targetId: autoComplete.id || autoComplete.key,
                      targetKind: 'field',
                      targetName: autoComplete.name || autoComplete.key,
                      ruleKind: 'visibility',
                  }
                : undefined,
        },
    );
    const { value: requiredFromFormula, settled: requiredFromFormulaSettled } = useFormulaStateWithStatus(
        autoComplete?.required,
        fields,
        formMethods,
        isEdit,
        isNew,
        activated,
        {
            fallback: false,
            ensureExternalDataSourceLoaded,
            trace: autoComplete
                ? {
                      targetId: autoComplete.id || autoComplete.key,
                      targetKind: 'field',
                      targetName: autoComplete.name || autoComplete.key,
                      ruleKind: 'required',
                  }
                : undefined,
        },
    );
    const readOnlyFromFormula = useFormulaState(autoComplete?.readonly, fields, formMethods, isEdit, isNew, activated, {
        fallback: false,
        ensureExternalDataSourceLoaded,
        trace: autoComplete
            ? {
                  targetId: autoComplete.id || autoComplete.key,
                  targetKind: 'field',
                  targetName: autoComplete.name || autoComplete.key,
                  ruleKind: 'readonly',
              }
            : undefined,
    });
    // Дебаг-оверрайды поля (жучок): forced-значения правил мимо формул.
    const visibility = fieldOverride?.visibility ?? visibilityFromFormula;
    const required = fieldOverride?.required ?? requiredFromFormula;
    const requiredSettled = fieldOverride?.required !== undefined || requiredFromFormulaSettled;
    const readOnly = fieldOverride?.readonly ?? readOnlyFromFormula;
    const onSaveField = useFieldSaver(fields, formMethods);
    const runFieldChanges = useRunFieldChanges();
    const fieldBinding = useMemo(() => ({ key: autoComplete?.key ?? '' }), [autoComplete?.key]);
    useRegisterFieldChanges(fieldBinding, autoComplete?.changes!, autoComplete?.key ?? '', fields, isEdit, isNew, onSaveField, setError, formMethods, undefined, Boolean(autoComplete && visibility));

    const findOptions = async (text: string) => {
        return await getOptions(autoComplete?.dataSource!, text);
    };

    const onInputChanged = async (value: string) => {
        await runFieldChanges(
            autoComplete?.changes!,
            value,
            autoComplete?.key!,
            fields,
            isEdit,
            isNew,
            onSaveField,
            setError,
            undefined,
            undefined,
            formMethods,
            fieldBinding,
        );
    };

    const dataField = fields[autoComplete?.key!];
    const idField = dataField?.name;

    useEffect(() => {
        if (dataField?.index !== undefined && !includedFields.includes(dataField.index)) {
            includedFields.push(dataField.index);
        }
    }, [dataField?.index, includedFields]);

    useClearErrorOnOptional(required, requiredSettled, formMethods, dataField);

    const effectiveRequired =
        debugSettings.skipAllValidation || debugSettings.skipRequiredValidation ? false : required;

    const renderView = (value: any) =>
        !autoComplete ? null : (
            <FieldWrapper
                isViewMode={!isEdit && !isNew}
                isEmpty={isEmptyDisplayValue(value)}
                inLineFormat={autoComplete.isValue}
                label={autoComplete.name}
                title={autoComplete.viewTitle}
                verticalBottomAlign={autoComplete?.verticalBottomAlign}
            >
                <DisplayField type={ValueType.Text} label={autoComplete.name} value={value} />
            </FieldWrapper>
        );

    return autoComplete && autoComplete.key && visibility && idField != undefined ? (
        <div
            className={clsx(
                'form-field',
                (isEdit || isNew) && autoComplete.displayMode !== 'view' && readOnly && 'form-field--readonly',
            )}
            data-testid={autoComplete.id ? `auto-complete-${autoComplete.id}` : undefined}
            data-document-field-index={dataField.index}
            {...formulaTraceAttrs(autoComplete.id || autoComplete.key, 'field')}
        >
            {debugSettings.showFieldFormulas && (
                <FieldFormulaTooltip
                    required={autoComplete.required}
                    visibilityRules={autoComplete.visibilityRules}
                    readonly={autoComplete.readonly}
                    fieldKey={autoComplete.key}
                    isViewMode={!isEdit && !isNew}
                    rhfPath={dataField?.name}
                    value={dataField?.value}
                    requiredResult={requiredFromFormula}
                    readonlyResult={readOnlyFromFormula}
                    visibilityResult={visibilityFromFormula}
                />
            )}
            {(isEdit || isNew) && autoComplete.displayMode === 'view' ? (
                <FormFieldView
                    name={idField!}
                    control={formMethods.control}
                    rules={{
                        required: {
                            value: effectiveRequired,
                            message: getDocumentRequiredMessage(autoComplete, 'autoComplete'),
                        },
                    }}
                >
                    {renderView}
                </FormFieldView>
            ) : isEdit || isNew ? (
                <AutoCompleteControl
                    label={autoComplete?.name}
                    tooltip={<ToolTipTitle title={autoComplete?.title} />}
                    formGroupProps={
                        {
                            verticalBottomAlign: autoComplete?.verticalBottomAlign,
                            reserveMessage,
                        } as IPartialFormGroup
                    }
                    disabled={readOnly}
                    required={effectiveRequired}
                    rules={{
                        required: {
                            value: effectiveRequired ? effectiveRequired : false,
                            message: getDocumentRequiredMessage(autoComplete ?? {}, 'autoComplete'),
                        },
                    }}
                    name={idField}
                    control={formMethods.control}
                    onValueChange={onInputChanged}
                    findOptions={findOptions}
                    placeholder={autoComplete?.placeholder ? autoComplete.placeholder : undefined}
                />
            ) : (
                renderView(dataField?.value)
            )}
        </div>
    ) : (
        <></>
    );
};

export default AutoComplete;
